// AutoRegisterRuleListPage — 自動登録ルール必要リスト (Phase 3 Reader A 設計準拠)
// 2026-04-22 Reconciler
//
// freee /api/1/user_matchers は OAuth 403 のため、FREEEMAN は「候補抽出 + user 手動 mark」で
// ルール設定状況を観察的に管理する。本ページは:
//   - wallet_txn_cache から rule_matched=0 の頻発 partner を候補抽出 (GET /candidates)
//   - Vibes 14列テーブル + Side Panel で表示
//   - mark-registered/mark-unregistered で D1 state 更新 (freee 側 rule 作成は user が手動で実施)
//   - observation_state で「登録済みと宣言後、実際に matched=1 になっているか」 を追跡
//
// Hash: #rules / #rules/:walletableId / #rules/:walletableId/:fingerprint

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  MdMenuBook,
  MdAttachMoney,
  MdBusiness,
  MdLocalOffer,
  MdInventory2,
  MdReceipt,
  MdNoteAlt,
  MdArrowForward,
  MdSettingsSuggest,
  MdAddCircle,
  MdSync,
  MdCheck,
  MdClose,
  MdWarning,
  MdOpenInNew,
  MdExpandMore,
  MdChevronRight,
} from 'react-icons/md';
import {
  Container,
  PageTitle,
  SubSectionTitle,
  CardBase,
  ColumnBase,
  HStack,
  VStack,
  Button,
  SearchField,
  SelectBox,
  CheckBox,
  Text,
  Note,
  StatusIcon,
  Breadcrumbs,
  MessageBlock,
  FloatingMessageBlock,
  TabBar,
} from '@freee_jp/vibes';
import {
  fetchAllFreeeUserMatchers,
  USER_MATCHER_ACT_NAMES,
  USER_MATCHER_CONDITION_NAMES,
  type FreeeUserMatcher,
  createFreeeUserMatcher,
  deleteFreeeUserMatcher,
  fetchAllFreeePartners,
  type FreeePartnerLite,
  type CreateUserMatcherBody,
  ACCOUNT_ITEM_NAMES,
  SECTION_NAMES_MAP,
  TAX_CODE_NAMES,
  fetchAutoRegisterRuleCandidates,
  markAutoRegisterRulesRegistered,
  markAutoRegisterRuleUnregistered,
  fetchAutoRegisterRuleEvidence,
  openSessionForBank,
  AutoRegisterRuleCandidate,
  AutoRegisterRuleEvidence,
  MarkRegisteredTarget,
} from '../api/client';

// ---------------------------------------------------------------------------
// Walletable metadata (BankProcessingPage WALLETABLES と同一)
// ---------------------------------------------------------------------------

interface WalletableMeta {
  walletable_id: number;
  walletable_type: 'bank_account' | 'credit_card' | 'wallet';
  name: string;
  tier: 'HIGH' | 'MID' | 'LOW' | 'DORMANT';
}

const WALLETABLES: WalletableMeta[] = [
  { walletable_id: 3838033, walletable_type: 'bank_account', name: '三井住友 普通', tier: 'HIGH' },
  { walletable_id: 3838050, walletable_type: 'bank_account', name: '三井住友 新宿', tier: 'HIGH' },
  { walletable_id: 2122229, walletable_type: 'credit_card', name: '三井住友VISA', tier: 'HIGH' },
  { walletable_id: 3838074, walletable_type: 'bank_account', name: 'UFJ 渋谷', tier: 'MID' },
  { walletable_id: 3838203, walletable_type: 'bank_account', name: 'UFJ 喜乃字屋', tier: 'MID' },
  { walletable_id: 3838100, walletable_type: 'bank_account', name: '西武信金 現金管理', tier: 'MID' },
  { walletable_id: 3845767, walletable_type: 'bank_account', name: '商工中金', tier: 'MID' },
  { walletable_id: 3838051, walletable_type: 'bank_account', name: '三井住友 当座', tier: 'LOW' },
  { walletable_id: 3838049, walletable_type: 'bank_account', name: '三井住友 現金管理', tier: 'LOW' },
  { walletable_id: 3838184, walletable_type: 'bank_account', name: 'UFJ 新宿', tier: 'LOW' },
  { walletable_id: 6447554, walletable_type: 'wallet', name: '未払費用 Amazon', tier: 'LOW' },
  { walletable_id: 3838143, walletable_type: 'bank_account', name: '西武信金 当座', tier: 'DORMANT' },
  { walletable_id: 3838144, walletable_type: 'bank_account', name: '西武信金 普通', tier: 'DORMANT' },
  { walletable_id: 3838176, walletable_type: 'bank_account', name: 'UFJ 秋葉原', tier: 'DORMANT' },
  { walletable_id: 3843633, walletable_type: 'bank_account', name: 'PayPay', tier: 'DORMANT' },
  { walletable_id: 2122272, walletable_type: 'credit_card', name: 'JCB', tier: 'DORMANT' },
  { walletable_id: 6542846, walletable_type: 'wallet', name: 'Square(API)', tier: 'DORMANT' },
  { walletable_id: 6543034, walletable_type: 'wallet', name: 'Square 売掛金', tier: 'DORMANT' },
  { walletable_id: 6543035, walletable_type: 'wallet', name: 'Square その他', tier: 'DORMANT' },
];

const FREEE_COMPANY_ID = 11518223;
const freeeRuleUrl = (walletableId: number) =>
  `https://secure.freee.co.jp/wallet_txns/automatic_rules?company_id=${FREEE_COMPANY_ID}&walletable_id=${walletableId}`;

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { name: 'すべて', value: 'all' },
  { name: '未設定', value: 'unregistered' },
  { name: '設定済', value: 'registered' },
];

const WTYPE_OPTIONS = [
  { name: 'すべての口座種別', value: 'all' },
  { name: '銀行口座', value: 'bank_account' },
  { name: 'クレジットカード', value: 'credit_card' },
  { name: 'ウォレット', value: 'wallet' },
];

const SORT_OPTIONS = [
  { name: '頻度順', value: 'frequency' },
  { name: '最新発生順', value: 'latest' },
  { name: '合計金額順', value: 'amount' },
  { name: '信頼度順', value: 'confidence' },
];

// ---------------------------------------------------------------------------
// Styles — BankProcessingPage の S オブジェクトをベースに流用
// ---------------------------------------------------------------------------

const S = {
  page: {
    background: '#f7f5f5',
    minHeight: '100vh',
    paddingBottom: '3rem',
  } as React.CSSProperties,

  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '12px',
    marginBottom: '20px',
  } as React.CSSProperties,

  kpiCard: {
    position: 'relative' as const,
    overflow: 'hidden',
  } as React.CSSProperties,

  kpiAccent: (color: string) => ({
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '3px',
    height: '100%',
    background: color,
    borderRadius: '3px 0 0 3px',
  }),

  mainLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0',
    transition: 'grid-template-columns 0.25s ease',
  } as React.CSSProperties,

  mainLayoutWithPanel: {
    display: 'grid',
    gridTemplateColumns: '62% 38%',
    gap: '16px',
    alignItems: 'stretch',
  } as React.CSSProperties,

  thCell: {
    padding: '10px 12px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: '#8a8580',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap' as const,
    borderBottom: '2px solid #dbd9d7',
    background: '#fafaf8',
  } as React.CSSProperties,

  tdCell: {
    padding: '12px 12px',
    fontSize: '0.8125rem',
    verticalAlign: 'middle' as const,
    borderBottom: '1px solid #edebea',
  } as React.CSSProperties,

  row: (isSelected: boolean, isActive: boolean) => ({
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    background: isActive ? '#ebf3ff' : isSelected ? '#f0f8ff' : 'transparent',
  }),

  badge: (bg: string, color: string) => ({
    display: 'inline-block',
    fontSize: '0.625rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    lineHeight: '1.4',
    background: bg,
    color,
    whiteSpace: 'nowrap' as const,
  }),

  flagBadge: {
    display: 'inline-block',
    fontSize: '0.5625rem',
    fontWeight: 600,
    padding: '1px 7px',
    borderRadius: '10px',
    background: '#fff3e0',
    color: '#c67320',
    marginRight: '3px',
    marginTop: '2px',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  sidePanel: {
    position: 'sticky' as const,
    top: '12px',
    maxHeight: 'calc(100vh - 24px)',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    background: '#fff',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #e5e4e2',
  } as React.CSSProperties,

  sidePanelHeader: {
    padding: '16px 20px 12px',
    borderBottom: '1px solid #edebea',
    background: '#fff',
  } as React.CSSProperties,

  sidePanelBody: {
    flex: 1,
    overflowY: 'auto' as const,
    background: '#fff',
  } as React.CSSProperties,

  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '6px 0',
    borderBottom: '1px solid #f5f3f2',
    fontSize: '0.75rem',
  } as React.CSSProperties,

  metaLabel: {
    color: '#8a8580',
    fontSize: '0.6875rem',
    flexShrink: 0,
    marginRight: '12px',
  } as React.CSSProperties,

  metaValue: {
    color: '#252525',
    fontSize: '0.75rem',
    fontWeight: 500,
    textAlign: 'right' as const,
    wordBreak: 'break-all' as const,
  } as React.CSSProperties,

  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  detailTh: {
    padding: '6px 10px',
    fontSize: '0.625rem',
    fontWeight: 600,
    color: '#8a8580',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    borderBottom: '1px solid #dbd9d7',
    background: '#f7f5f5',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  detailTd: {
    padding: '8px 10px',
    fontSize: '0.75rem',
    borderBottom: '1px solid #edebea',
    verticalAlign: 'top' as const,
  } as React.CSSProperties,

  stepNumBadge: {
    display: 'inline-block',
    minWidth: '24px',
    height: '24px',
    lineHeight: '24px',
    padding: '0 8px',
    background: '#e8f0fe',
    color: '#2e7cf6',
    borderRadius: '12px',
    fontWeight: 700,
    fontSize: '0.75rem',
    textAlign: 'center' as const,
    marginRight: '8px',
  } as React.CSSProperties,

  summaryFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    marginTop: '8px',
  } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// Hash parsing
// ---------------------------------------------------------------------------

interface RulesHash {
  walletableId: number | null;
  fingerprint: string | null;
  status: string | null;
}

function parseRulesHash(): RulesHash {
  if (typeof window === 'undefined') return { walletableId: null, fingerprint: null, status: null };
  const h = (window.location.hash || '').toLowerCase();
  // Supported forms:
  //   #rules
  //   #rules/unregistered  or  #rules/registered
  //   #rules/:walletableId
  //   #rules/:walletableId/:fingerprint
  const m = h.match(/^#?rules(?:\/([\w-]+))?(?:\/([\w-]+))?$/);
  if (!m) return { walletableId: null, fingerprint: null, status: null };
  const seg1 = m[1] || '';
  const seg2 = m[2] || '';
  if (seg1 === 'unregistered' || seg1 === 'registered' || seg1 === 'pending') {
    return { walletableId: null, fingerprint: null, status: seg1 };
  }
  const wid = /^\d+$/.test(seg1) ? Number(seg1) : null;
  const fp = seg2 || null;
  return { walletableId: wid, fingerprint: fp, status: null };
}

function buildRulesHash(walletableId: number | null, fingerprint: string | null): string {
  let h = '#rules';
  if (walletableId) h += `/${walletableId}`;
  if (fingerprint) h += `/${fingerprint}`;
  return h;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n: number | null | undefined) => (n == null ? '—' : '¥' + Math.abs(n).toLocaleString('ja-JP'));

const walletableName = (id: number | null): string => {
  if (!id) return '全口座';
  const w = WALLETABLES.find((x) => x.walletable_id === id);
  return w ? w.name : `wallet_id:${id}`;
};

const walletableTier = (id: number | null): WalletableMeta['tier'] | null => {
  if (!id) return null;
  const w = WALLETABLES.find((x) => x.walletable_id === id);
  return w?.tier || null;
};

const tierColor = (tier: WalletableMeta['tier']): { bg: string; color: string } => {
  switch (tier) {
    case 'HIGH': return { bg: '#fff3e0', color: '#c67320' };
    case 'MID': return { bg: '#fff8e1', color: '#d7a400' };
    case 'LOW': return { bg: '#f5f3f2', color: '#8a8580' };
    case 'DORMANT': return { bg: '#ededed', color: '#a0a0a0' };
  }
};

const confidenceColor = (c: number | null | undefined) => {
  if (c == null) return '#c4c0bc';
  if (c >= 0.92) return '#338650';
  if (c >= 0.75) return '#c67320';
  return '#c33939';
};

const frequencyColor = (monthlyAvg: number | null | undefined) => {
  if (monthlyAvg == null) return '#8a8580';
  if (monthlyAvg >= 5) return '#338650';
  if (monthlyAvg >= 3) return '#8a8580';
  return '#c67320';
};

type StatusKey = 'active' | 'manual_confirmed' | 'pending_confirmation' |
  'unregistered' | 'suspected_missing' | 'confirm_pending' | 'superseded';

const statusBadge = (status: string): { bg: string; color: string; label: string; icon: 'success' | 'warning' | 'error' | 'progress' } => {
  switch (status as StatusKey) {
    case 'active':
      return { bg: '#e6f4ea', color: '#338650', label: '設定済', icon: 'success' };
    case 'manual_confirmed':
      return { bg: '#e8f5e9', color: '#2e7d32', label: '設定済(手動)', icon: 'success' };
    case 'pending_confirmation':
      // 統一語彙 (lib/statusVocab.ts): ルール化するか人が判定する = 対応待ち(判定) 橙
      return { bg: '#fdf3e3', color: '#a46a12', label: '対応待ち(ルール判定)', icon: 'warning' };
    case 'suspected_missing':
      return { bg: '#fde7e7', color: '#c33939', label: '未設定疑い', icon: 'error' };
    case 'confirm_pending':
      return { bg: '#fff4e5', color: '#dd7a00', label: '設定疑念', icon: 'warning' };
    case 'superseded':
      return { bg: '#ededed', color: '#a0a0a0', label: '廃止', icon: 'progress' };
    case 'unregistered':
    default:
      return { bg: '#fde7e7', color: '#c33939', label: '未設定', icon: 'error' };
  }
};

const flowTypeLabel = (ft: string): string => {
  switch (ft) {
    case 'bank_auto_debit': return '引落';
    case 'bank_transfer_in': return '入金';
    case 'card_payment': return 'カード';
    case 'transfer': return '振替';
    default: return ft;
  }
};

const flowTypeColor = (ft: string): { bg: string; color: string } => {
  switch (ft) {
    case 'bank_auto_debit': return { bg: '#fff3e0', color: '#c67320' };
    case 'bank_transfer_in': return { bg: '#e6f4ea', color: '#338650' };
    case 'card_payment': return { bg: '#e8f0fe', color: '#2e7cf6' };
    default: return { bg: '#f5f3f2', color: '#8a8580' };
  }
};

interface Toast {
  id: number;
  kind: 'success' | 'error' | 'notice' | 'info';
  text: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AutoRegisterRuleListPage: React.FC = () => {
  const initialHash = useMemo(() => parseRulesHash(), []);

  const [selectedWalletable, setSelectedWalletable] = useState<number | null>(initialHash.walletableId);
  const [selectedWalletableType, setSelectedWalletableType] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>(initialHash.status || 'all');
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState<'frequency' | 'latest' | 'amount' | 'confidence'>('frequency');
  const [onlyRegistrySeed, setOnlyRegistrySeed] = useState(false);
  const [minOccurrence, setMinOccurrence] = useState<number>(3);

  const [rows, setRows] = useState<AutoRegisterRuleCandidate[]>([]);
  const [loading, setLoading] = useState(false);

  // freee 既存 user_matchers (= 自動登録ルール) state
  const [freeeMatchers, setFreeeMatchers] = useState<FreeeUserMatcher[]>([]);
  const [freeeLoading, setFreeeLoading] = useState(false);
  const [freeeError, setFreeeError] = useState<string | null>(null);
  const [freeeFilter, setFreeeFilter] = useState('');
  const [freeeActFilter, setFreeeActFilter] = useState<number | 'all'>('all');
  const [freeeShowAll, setFreeeShowAll] = useState(false);

  // 新規 ルール作成 form state
  const [partnersList, setPartnersList] = useState<FreeePartnerLite[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formAct, setFormAct] = useState<number>(1); // default: 取引登録 (= 完全自動)
  const [formActive, setFormActive] = useState(true);
  const [formCondition, setFormCondition] = useState<number>(0); // default: partial
  const [formDescription, setFormDescription] = useState('');
  const [formEntrySide, setFormEntrySide] = useState<'income' | 'expense'>('expense');
  const [formPriority, setFormPriority] = useState<number>(50);
  const [formWalletable, setFormWalletable] = useState('');
  const [formMinAmount, setFormMinAmount] = useState('');
  const [formMaxAmount, setFormMaxAmount] = useState('');
  const [formAccountItemName, setFormAccountItemName] = useState('');
  const [formPartnerName, setFormPartnerName] = useState('');
  const [formSectionName, setFormSectionName] = useState('');
  const [formTaxName, setFormTaxName] = useState('');
  const [formDealDescription, setFormDealDescription] = useState('');
  // === advanced fields (= 全 22 spec 完全対応) ===
  const [formItemName, setFormItemName] = useState('');
  const [formTransferWalletable, setFormTransferWalletable] = useState('');
  const [formQualifiedInvoice, setFormQualifiedInvoice] = useState<'non_qualified' | 'qualified' | 'depends_on_partner' | ''>('');
  const [formCardLabel, setFormCardLabel] = useState('');
  const [formDivisionTag1, setFormDivisionTag1] = useState('');
  const [formDivisionTag2, setFormDivisionTag2] = useState('');
  const [formDivisionTag3, setFormDivisionTag3] = useState('');
  const [formDefaultTagNames, setFormDefaultTagNames] = useState('');  // comma-separated
  const [formSuggestTaxFromWalletable, setFormSuggestTaxFromWalletable] = useState(false);
  const [formAdvancedOpen, setFormAdvancedOpen] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [selectedFingerprints, setSelectedFingerprints] = useState<Set<string>>(new Set());
  const [activePanelFp, setActivePanelFp] = useState<string | null>(initialHash.fingerprint);
  const [sidePanelTab, setSidePanelTab] = useState(0);

  const [panelEvidence, setPanelEvidence] = useState<AutoRegisterRuleEvidence | null>(null);

  const [workingFps, setWorkingFps] = useState<Set<string>>(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const [toasts, setToasts] = useState<Toast[]>([]);
  const pushToast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 6000);
  }, []);

  // -------------------------------------------------------------------------
  // Session open (lazy)
  // -------------------------------------------------------------------------
  const ensureSession = useCallback(async (): Promise<string | null> => {
    if (sessionId) return sessionId;
    try {
      const s = await openSessionForBank();
      setSessionId(s.session_id);
      return s.session_id;
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `session open failed: ${e instanceof Error ? e.message : String(e)}` });
      return null;
    }
  }, [sessionId, pushToast]);

  // -------------------------------------------------------------------------
  // Load rows
  // -------------------------------------------------------------------------
  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof fetchAutoRegisterRuleCandidates>[0] = {
        min_occurrence: minOccurrence,
        months: 6,
      };
      if (selectedWalletable) params.walletable_id = selectedWalletable;
      if (selectedWalletableType !== 'all') params.walletable_type = selectedWalletableType;
      if (statusFilter !== 'all') params.status_filter = statusFilter as 'unregistered' | 'registered';
      if (searchText) params.search = searchText;

      const res = await fetchAutoRegisterRuleCandidates(params);
      setRows(res.items);
      setLastLoadedAt(new Date().toISOString().slice(0, 19).replace('T', ' '));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [selectedWalletable, selectedWalletableType, statusFilter, searchText, minOccurrence]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // freee user_matchers 取得 (= page 開いたら 1 回 fetch)
  const loadFreeeMatchers = useCallback(async () => {
    setFreeeLoading(true);
    setFreeeError(null);
    try {
      const all = await fetchAllFreeeUserMatchers();
      setFreeeMatchers(all);
    } catch (e: unknown) {
      setFreeeError(e instanceof Error ? e.message : String(e));
    } finally {
      setFreeeLoading(false);
    }
  }, []);
  useEffect(() => { loadFreeeMatchers(); }, [loadFreeeMatchers]);

  // partners list (= dropdown用、 typo による auto-tag-create を防止)
  useEffect(() => {
    setPartnersLoading(true);
    fetchAllFreeePartners()
      .then((partners) => setPartnersList(partners))
      .catch(() => setPartnersList([]))
      .finally(() => setPartnersLoading(false));
  }, []);

  // walletables for form (= dropdown)
  const formWalletableOptions = useMemo(() => [
    { value: '', name: '指定なし (= 全口座対象)' },
    ...WALLETABLES.map(w => ({ value: w.name, name: `[${w.tier}] ${w.name}` })),
  ], []);

  const formAccountItemOptions = useMemo(() => [
    { value: '', name: '— 選択 —' },
    ...Object.entries(ACCOUNT_ITEM_NAMES).map(([id, name]) => ({ value: name, name })),
  ], []);

  const formSectionOptions = useMemo(() => [
    { value: '', name: '指定なし' },
    ...Object.entries(SECTION_NAMES_MAP).map(([id, name]) => ({ value: name, name })),
  ], []);

  const formTaxOptions = useMemo(() => [
    { value: '', name: '— 選択 —' },
    ...Object.entries(TAX_CODE_NAMES).map(([code, name]) => ({ value: name, name: `${code}: ${name}` })),
  ], []);

  const formActOptions = useMemo(() => Object.entries(USER_MATCHER_ACT_NAMES)
    .filter(([k]) => [0, 1, 2, 3, 4, 10, 11, 12].includes(Number(k)))
    .map(([k, v]) => ({ value: k, name: `${k}: ${v}` })), []);

  const formConditionOptions = useMemo(() => Object.entries(USER_MATCHER_CONDITION_NAMES)
    .map(([k, v]) => ({ value: k, name: `${k}: ${v}` })), []);

  const resetForm = useCallback(() => {
    setFormAct(1);
    setFormActive(true);
    setFormCondition(0);
    setFormDescription('');
    setFormEntrySide('expense');
    setFormPriority(50);
    setFormWalletable('');
    setFormMinAmount('');
    setFormMaxAmount('');
    setFormAccountItemName('');
    setFormPartnerName('');
    setFormSectionName('');
    setFormTaxName('');
    setFormDealDescription('');
    setFormItemName('');
    setFormTransferWalletable('');
    setFormQualifiedInvoice('');
    setFormCardLabel('');
    setFormDivisionTag1('');
    setFormDivisionTag2('');
    setFormDivisionTag3('');
    setFormDefaultTagNames('');
    setFormSuggestTaxFromWalletable(false);
    setFormError(null);
  }, []);

  const submitForm = useCallback(async () => {
    setFormError(null);
    // validation
    if (!formDescription.trim()) {
      setFormError('摘要 pattern (description) が必須です');
      return;
    }
    // === Codex review #6: API 非対応 act guardrail (= 5/6/7/8/9) ===
    if ([5, 6, 7, 8, 9].includes(formAct)) {
      setFormError(`act=${formAct} は user_matchers API 非対応 (= manual_template/manual_scrub/auto_scrub/etc)。 freee UI から作成してください`);
      return;
    }
    if ([0, 1].includes(formAct)) {
      if (!formAccountItemName) { setFormError('act=取引推測/登録 では 勘定科目 必須'); return; }
      if (!formTaxName) { setFormError('act=取引推測/登録 では 税区分 必須'); return; }
    }
    // === Codex review #6: 重複作成防止 ===
    const dup = freeeMatchers.find(m =>
      m.entry_side_str === formEntrySide &&
      m.description?.toLowerCase() === formDescription.trim().toLowerCase() &&
      m.act === formAct
    );
    if (dup) {
      const ok = window.confirm(
        `[警告] 既存 rule id=${dup.id} と description+act+entry_side が完全一致です。\n` +
        `「${dup.description}」 → ${dup.account_item_name || '?'} / ${dup.partner_name || '?'}\n\n` +
        `重複作成すると freee 側で同じ pattern が 2 つの rule にマッチ → 動作不確定。\n` +
        `本当に進めますか? (推奨: キャンセル → 既存 rule を編集)`
      );
      if (!ok) return;
    }
    // partner_name typo 警告 (= freee で auto-create 防止、 self-review 強化版)
    if (formPartnerName && partnersList.length > 0) {
      const exists = partnersList.some(p =>
        p.name === formPartnerName || p.long_name === formPartnerName || p.shortcut1 === formPartnerName);
      if (!exists) {
        const ok = window.confirm(
          `[警告] 「${formPartnerName}」 は freee 未登録 取引先です。\n\n` +
          `freee dashboard で先に取引先を作成 → ここで再試行を **強推奨**。\n\n` +
          `このまま進めると freee が **新規 取引先タグを 自動作成** します。\n` +
          `内部検索性低下、 重複作成リスク、 帳票上の partner_id ずれ。\n\n` +
          `本当にこのまま進めますか? (= 推奨: キャンセル → freee で partner 登録)`
        );
        if (!ok) return;
      }
    }

    setFormSubmitting(true);
    try {
      const body: CreateUserMatcherBody = {
        act: formAct,
        active: formActive,
        condition: formCondition,
        description: formDescription.trim(),
        entry_side_str: formEntrySide,
        priority: formPriority,
        walletable: formWalletable || null,
        min_amount: formMinAmount ? Number(formMinAmount) : null,
        max_amount: formMaxAmount ? Number(formMaxAmount) : null,
        account_item_name: formAccountItemName || null,
        partner_name: formPartnerName || null,
        section_name: formSectionName || null,
        tax_name: formTaxName || null,
        deal_description: formDealDescription || null,
        // advanced fields
        item_name: formItemName || null,
        transfer_walletable: formTransferWalletable || null,
        qualified_invoice_setting: formQualifiedInvoice || null,
        card_label: formCardLabel || null,
        division_tag_1_name: formDivisionTag1 || null,
        division_tag_2_name: formDivisionTag2 || null,
        division_tag_3_name: formDivisionTag3 || null,
        default_tag_names: formDefaultTagNames
          ? formDefaultTagNames.split(',').map(s => s.trim()).filter(Boolean)
          : null,
        suggest_tax_from_walletable_invoice: formSuggestTaxFromWalletable || null,
      };
      const r = await createFreeeUserMatcher(body);
      if (!r.ok) {
        setFormError(`作成失敗: ${JSON.stringify(r.error || r)}`);
        pushToast({ kind: 'error', text: `自動登録ルール 作成失敗` });
        return;
      }
      pushToast({ kind: 'success', text: `自動登録ルール 作成成功 (id=${r.user_matcher?.id || r.data?.id || '?'})` });
      resetForm();
      setFormOpen(false);
      await loadFreeeMatchers();  // 一覧を refresh
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setFormSubmitting(false);
    }
  }, [formAct, formActive, formCondition, formDescription, formEntrySide, formPriority,
      formWalletable, formMinAmount, formMaxAmount, formAccountItemName, formPartnerName,
      formSectionName, formTaxName, formDealDescription, partnersList, freeeMatchers,
      formItemName, formTransferWalletable, formQualifiedInvoice, formCardLabel,
      formDivisionTag1, formDivisionTag2, formDivisionTag3, formDefaultTagNames,
      formSuggestTaxFromWalletable,
      resetForm, loadFreeeMatchers, pushToast]);

  const onDeleteMatcher = useCallback(async (m: FreeeUserMatcher) => {
    if (!window.confirm(`ルール「${m.description}」 (id=${m.id}) を削除しますか?`)) return;
    try {
      const r = await deleteFreeeUserMatcher(m.id);
      if (r.ok) {
        pushToast({ kind: 'success', text: `ルール削除成功 (id=${m.id})` });
        await loadFreeeMatchers();
      } else {
        pushToast({ kind: 'error', text: `削除失敗 status=${r.status}` });
      }
    } catch (e) {
      pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
    }
  }, [pushToast, loadFreeeMatchers]);

  // freee matchers filter (= search + act filter)
  const filteredFreeeMatchers = useMemo(() => {
    let arr = freeeMatchers;
    if (freeeActFilter !== 'all') arr = arr.filter(m => m.act === freeeActFilter);
    if (freeeFilter) {
      const q = freeeFilter.toLowerCase();
      arr = arr.filter(m =>
        (m.description || '').toLowerCase().includes(q) ||
        (m.partner_name || '').toLowerCase().includes(q) ||
        (m.account_item_name || '').toLowerCase().includes(q) ||
        (m.walletable || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [freeeMatchers, freeeFilter, freeeActFilter]);

  // -------------------------------------------------------------------------
  // Evidence load on panel open
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!activePanelFp) {
      setPanelEvidence(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const e = await fetchAutoRegisterRuleEvidence(activePanelFp, 20);
        if (alive) setPanelEvidence(e);
      } catch (e: unknown) {
        // evidence not yet available (candidate-only) — skip quietly
        if (alive) setPanelEvidence(null);
      }
    })();
    return () => { alive = false; };
  }, [activePanelFp]);

  // Hash sync
  useEffect(() => {
    const hash = buildRulesHash(selectedWalletable, activePanelFp);
    if (window.location.hash !== hash) {
      window.history.replaceState(null, '', hash);
    }
  }, [selectedWalletable, activePanelFp]);

  // -------------------------------------------------------------------------
  // Filtered + sorted rows (client-side)
  // -------------------------------------------------------------------------
  const filtered = useMemo(() => {
    let list = [...rows];
    if (onlyRegistrySeed) {
      list = list.filter((r) => r.registry != null);
    }
    // Sort
    list.sort((a, b) => {
      if (sortBy === 'frequency') {
        return b.occurrence.count_window - a.occurrence.count_window;
      }
      if (sortBy === 'latest') {
        return (b.occurrence.last_date || '').localeCompare(a.occurrence.last_date || '');
      }
      if (sortBy === 'amount') {
        return Math.abs(b.amount_stats.total) - Math.abs(a.amount_stats.total);
      }
      if (sortBy === 'confidence') {
        return b.pattern_confidence - a.pattern_confidence;
      }
      return 0;
    });
    return list;
  }, [rows, onlyRegistrySeed, sortBy]);

  // KPI stats
  const stats = useMemo(() => {
    const bag = {
      total: rows.length,
      unregistered: 0,
      registered: 0,
      pending: 0,
      thisMonth: 0,
    };
    const thisMonth = new Date().toISOString().slice(0, 7);
    for (const r of rows) {
      const st = r.status;
      if (st === 'active' || st === 'manual_confirmed') bag.registered += 1;
      else if (st === 'pending_confirmation' || st === 'confirm_pending' || st === 'suspected_missing') bag.pending += 1;
      else bag.unregistered += 1;
      if (r.occurrence.last_date && r.occurrence.last_date.startsWith(thisMonth)) {
        bag.thisMonth += 1;
      }
    }
    return bag;
  }, [rows]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const toggleSelect = useCallback((fp: string) => {
    setSelectedFingerprints((prev) => {
      const next = new Set(prev);
      if (next.has(fp)) next.delete(fp);
      else next.add(fp);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedFingerprints((prev) => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map((r) => r.rule_fingerprint));
    });
  }, [filtered]);

  const handleRowClick = useCallback((r: AutoRegisterRuleCandidate) => {
    if (activePanelFp === r.rule_fingerprint) {
      setActivePanelFp(null);
    } else {
      setActivePanelFp(r.rule_fingerprint);
      setSidePanelTab(0);
    }
  }, [activePanelFp]);

  const buildTargetFromCandidate = (r: AutoRegisterRuleCandidate): MarkRegisteredTarget => ({
    walletable_id: r.walletable_id,
    walletable_type: r.walletable_type,
    entry_side: r.entry_side,
    description_pattern: r.description_pattern,
    match_type: r.match_type,
    min_amount: r.amount_stats.min,
    max_amount: r.amount_stats.max,
    partner_id: r.partner_id,
    partner_name: r.partner_name,
    target_account_item_id: r.recommended.account_item_id,
    target_account_item_name: r.recommended.account_item_name,
    target_tax_code: r.recommended.tax_code,
    target_section_id: r.recommended.section_id,
    target_section_name: r.recommended.section_name,
    rule_fingerprint: r.rule_fingerprint,
  });

  const onMarkRegistered = useCallback(async (r: AutoRegisterRuleCandidate) => {
    if (!window.confirm(`${r.partner_name || r.description_pattern} を登録済みに変更します。freee 側のルール設定も完了していますか?`)) return;
    try {
      setWorkingFps((s) => { const n = new Set(s); n.add(r.rule_fingerprint); return n; });
      const sess = await ensureSession();
      const res = await markAutoRegisterRulesRegistered([buildTargetFromCandidate(r)], {
        changed_by: 'web_app_user',
        session_id: sess || undefined,
      });
      pushToast({
        kind: 'success',
        text: `登録済み化: ${res.updated}件 (${res.fingerprints[0]?.slice(0, 10) || 'n/a'}…)`,
      });
      setTimeout(() => { loadRows(); }, 1200);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setWorkingFps((s) => { const n = new Set(s); n.delete(r.rule_fingerprint); return n; });
    }
  }, [ensureSession, loadRows, pushToast]);

  const onMarkUnregistered = useCallback(async (r: AutoRegisterRuleCandidate) => {
    if (!window.confirm(`${r.partner_name || r.description_pattern} の設定済みフラグを取り消します。よろしいですか?`)) return;
    try {
      setWorkingFps((s) => { const n = new Set(s); n.add(r.rule_fingerprint); return n; });
      const sess = await ensureSession();
      const reason = window.prompt('取消理由 (任意)', '') || '';
      await markAutoRegisterRuleUnregistered(r.rule_fingerprint, {
        changed_by: 'web_app_user',
        reason,
        session_id: sess || undefined,
      });
      pushToast({ kind: 'success', text: `未設定に戻しました (${r.partner_name || r.description_pattern})` });
      setTimeout(() => { loadRows(); }, 1200);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setWorkingFps((s) => { const n = new Set(s); n.delete(r.rule_fingerprint); return n; });
    }
  }, [ensureSession, loadRows, pushToast]);

  const onBulkMarkRegistered = useCallback(async () => {
    const fps = Array.from(selectedFingerprints);
    const targets = fps
      .map((fp) => rows.find((r) => r.rule_fingerprint === fp))
      .filter((r): r is AutoRegisterRuleCandidate => !!r)
      .filter((r) => r.status !== 'active' && r.status !== 'manual_confirmed')
      .map(buildTargetFromCandidate);
    if (targets.length === 0) {
      pushToast({ kind: 'info', text: '登録可能な行がありません (active/manual_confirmed を除外)' });
      return;
    }
    if (!window.confirm(`${targets.length}件を登録済みに一括変更します。freee 側の設定は user 責任で完了してください。続行?`)) return;
    try {
      setBulkWorking(true);
      const sess = await ensureSession();
      const res = await markAutoRegisterRulesRegistered(targets, {
        changed_by: 'web_app_user',
        session_id: sess || undefined,
      });
      pushToast({ kind: 'success', text: `一括登録済み化: ${res.updated}/${res.count}件 成功` });
      setSelectedFingerprints(new Set());
      setTimeout(() => { loadRows(); }, 1500);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBulkWorking(false);
    }
  }, [selectedFingerprints, rows, ensureSession, loadRows, pushToast]);

  const onExportCsv = useCallback(() => {
    const headers = [
      'status', 'partner_id', 'partner_name', 'walletable_id', 'walletable_name',
      'flow_type', 'entry_side', 'description_pattern', 'match_type',
      'occurrence_count', 'monthly_avg', 'min_amount', 'max_amount',
      'account_item_name', 'tax_code', 'section_name', 'pattern_confidence', 'pattern_source',
    ];
    const lines = [headers.join(',')];
    for (const r of filtered) {
      const cells = [
        r.status,
        r.partner_id ?? '',
        r.partner_name ?? '',
        r.walletable_id,
        walletableName(r.walletable_id),
        r.flow_type,
        r.entry_side,
        r.description_pattern,
        r.match_type,
        r.occurrence.count_window,
        r.occurrence.monthly_avg,
        r.amount_stats.min,
        r.amount_stats.max,
        r.recommended.account_item_name ?? '',
        r.recommended.tax_code ?? '',
        r.recommended.section_name ?? '',
        r.pattern_confidence,
        r.pattern_source,
      ].map((c) => {
        const s = String(c);
        return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      });
      lines.push(cells.join(','));
    }
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `auto_register_rules_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [filtered]);

  const walletableOptions = useMemo(() => {
    const opts: { name: string; value: string }[] = [{ name: '全口座', value: 'all' }];
    WALLETABLES.filter((w) => selectedWalletableType === 'all' || w.walletable_type === selectedWalletableType).forEach((w) => {
      opts.push({ value: String(w.walletable_id), name: `[${w.tier}] ${w.name}` });
    });
    return opts;
  }, [selectedWalletableType]);

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderKpiCard = (label: string, value: string, accent: string, subtext?: string) => (
    <CardBase paddingSize="small" overflowHidden={false}>
      <div style={S.kpiCard}>
        <div style={S.kpiAccent(accent)} />
        <div style={{ paddingLeft: '8px' }}>
          <div style={{ fontSize: '0.6875rem', color: '#8a8580', marginBottom: '4px', fontWeight: 500 }}>{label}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#252525', lineHeight: 1.1 }}>{value}</div>
          {subtext && <div style={{ fontSize: '0.625rem', color: '#8a8580', marginTop: '4px' }}>{subtext}</div>}
        </div>
      </div>
    </CardBase>
  );

  const renderActionCell = (r: AutoRegisterRuleCandidate) => {
    const working = workingFps.has(r.rule_fingerprint);
    const isRegistered = r.status === 'active' || r.status === 'manual_confirmed';
    if (isRegistered) {
      return (
        <HStack gap={0.25}>
          <a
            href={freeeRuleUrl(r.walletable_id)}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ ...S.badge('#e6f4ea', '#338650'), textDecoration: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            freee で確認 <MdOpenInNew size={12} />
          </a>
          <Button
            small
            appearance="tertiary"
            disabled={working}
            onClick={(e: any) => { e.stopPropagation(); onMarkUnregistered(r); }}
          >
            未設定に戻す
          </Button>
        </HStack>
      );
    }
    return (
      <HStack gap={0.25}>
        <Button
          small
          appearance="primary"
          disabled={working}
          onClick={(e: any) => { e.stopPropagation(); prefillFromCandidate(r); }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <MdSettingsSuggest size={14} /> API で 自動 rule 化
          </span>
        </Button>
        <a
          href={freeeRuleUrl(r.walletable_id)}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ ...S.badge('#e8f0fe', '#2e7cf6'), textDecoration: 'none', cursor: 'pointer', fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
        >
          freee UI <MdOpenInNew size={11} />
        </a>
        <Button
          small
          appearance="tertiary"
          disabled={working}
          onClick={(e: any) => { e.stopPropagation(); onMarkRegistered(r); }}
        >
          登録済 mark
        </Button>
      </HStack>
    );
  };

  // 候補 → 新規 form 自動 prefill (= AI 推奨 flow、 Codex adversarial review v2 反映)
  const prefillFromCandidate = useCallback((r: AutoRegisterRuleCandidate) => {
    const walletableMatch = WALLETABLES.find(w => w.walletable_id === r.walletable_id);
    setFormAct(1);
    setFormActive(true);
    const desc = r.description_pattern || r.sample_description || '';

    // === Codex review #1 fix: condition は match_type を優先、 不在時のみ token-shape heuristic ===
    // token shape: 末尾に variable substring (= 数字/日付/branch code) があれば prefix
    //              全体 静的なら exact、 短ければ partial
    const condMap: Record<string, number> = { prefix: 1, suffix: 2, exact: 3, partial: 0 };
    let condition: number;
    if (r.match_type && condMap[r.match_type] != null) {
      condition = condMap[r.match_type];
    } else {
      const tail = desc.slice(-6);
      const hasVarTail = /[\d０-９]{2,}|\d+\/\d+|\d+月|支店|号/.test(tail);
      condition = hasVarTail ? 1 /* prefix */ : (desc.length >= 12 ? 3 /* exact */ : 0 /* partial */);
    }
    setFormCondition(condition);

    setFormDescription(desc);
    setFormEntrySide(r.entry_side);

    // === Codex review #4 fix: priority 最大値+10、 全 0 なら 10 から階層 ===
    const existingP = freeeMatchers.filter(m => m.act === 1 && m.priority != null).map(m => m.priority);
    const maxP = existingP.length ? Math.max(...existingP) : 0;
    setFormPriority(maxP === 0 ? 10 : Math.min(maxP + 10, 999));

    setFormWalletable(walletableMatch?.name || '');

    // === Codex review #2 fix: cv + sample size + absolute bounds ===
    const cv = r.amount_stats.cv;
    const sample = r.occurrence?.count_window || 0;
    const meanAmt = r.amount_stats.avg ?? r.amount_stats.min;
    const absRange = r.amount_stats.max - r.amount_stats.min;
    let buffer: number | null = null;
    if (sample < 5) {
      buffer = null;  // sample size 不足 → range 指定なし
    } else if (meanAmt > 0 && absRange / meanAmt < 0.02) {
      buffer = 0.05;  // 絶対変動 微小 → 5%
    } else if (cv < 0.1) buffer = 0.05;
    else if (cv < 0.3) buffer = 0.15;
    else if (cv < 0.5) buffer = 0.30;
    if (buffer != null && r.amount_stats.min) {
      const minBuf = Math.max(Math.floor(r.amount_stats.min * (1 - buffer)), 0);
      const maxBuf = Math.ceil(r.amount_stats.max * (1 + buffer));
      setFormMinAmount(String(minBuf));
      setFormMaxAmount(String(maxBuf));
    } else {
      setFormMinAmount('');
      setFormMaxAmount('');
    }

    setFormAccountItemName(r.recommended.account_item_name || '');
    setFormPartnerName(r.partner_name || '');
    setFormSectionName(r.recommended.section_name || '');
    if (r.recommended.tax_code != null) {
      setFormTaxName(TAX_CODE_NAMES[r.recommended.tax_code] || '');
    }
    setFormDealDescription(r.partner_name || '');
    setFormError(null);

    // === Codex review #6 fix: 既存 rule との重複/オーバーラップ検出 ===
    const dupCandidate = freeeMatchers.find(m =>
      m.entry_side_str === r.entry_side &&
      m.description?.toLowerCase() === desc.toLowerCase() &&
      m.act === 1
    );
    const overlapCandidate = !dupCandidate && freeeMatchers.find(m =>
      m.entry_side_str === r.entry_side &&
      m.act === 1 &&
      desc && m.description &&
      (desc.toLowerCase().includes(m.description.toLowerCase()) ||
       m.description.toLowerCase().includes(desc.toLowerCase()))
    );
    if (dupCandidate) {
      pushToast({
        kind: 'error',
        text: `[警告] 重複検出: 既存 rule id=${dupCandidate.id} と完全一致 description。 作成すると二重ルールになります`,
      });
      setFormError(`既存 rule id=${dupCandidate.id} (= "${dupCandidate.description}") と重複。 削除 or 編集を推奨`);
    } else if (overlapCandidate) {
      pushToast({
        kind: 'notice',
        text: `[注意] 部分一致 既存 rule id=${overlapCandidate.id} ("${overlapCandidate.description.slice(0,30)}") と overlap 可能性。 確認推奨`,
      });
    }

    setFormOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    pushToast({
      kind: 'info',
      text: `prefill: cv=${cv.toFixed(2)} sample=${sample} ${buffer != null ? `±${(buffer*100).toFixed(0)}%` : '範囲なし'} condition=${['部分','前方','後方','完全','指定なし'][condition]}一致`,
    });
  }, [freeeMatchers, pushToast]);

  // Side panel render
  const renderSidePanel = () => {
    if (!activePanelFp) return null;
    const r = filtered.find((x) => x.rule_fingerprint === activePanelFp) || rows.find((x) => x.rule_fingerprint === activePanelFp);
    if (!r) return null;
    const stBadge = statusBadge(r.status);
    const working = workingFps.has(r.rule_fingerprint);

    return (
      <div style={{ minHeight: '1px' }}>
        <div style={S.sidePanel}>
          {/* Header */}
          <div style={S.sidePanelHeader}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text weight="bold" size={0.875}>
                  {r.partner_name || r.description_pattern}
                </Text>
                <div style={{ marginTop: '4px' }}>
                  <Text size={0.75} color="burnt">{walletableName(r.walletable_id)}</Text>
                  <span style={{ margin: '0 6px', color: '#dbd9d7' }}>|</span>
                  <span style={S.badge(flowTypeColor(r.flow_type).bg, flowTypeColor(r.flow_type).color)}>
                    {flowTypeLabel(r.flow_type)}
                  </span>
                </div>
                <div style={{ marginTop: '4px' }}>
                  <StatusIcon type={stBadge.icon}>{stBadge.label}</StatusIcon>
                </div>
              </div>
              <Button small appearance="tertiary" onClick={() => setActivePanelFp(null)}>
                閉じる
              </Button>
            </div>
            <div style={{ marginTop: '8px' }}>
              <TabBar
                tabs={['詳細', '設定手順', '履歴']}
                currentTabIndex={sidePanelTab}
                onClickTab={(i) => setSidePanelTab(i)}
                small
              />
            </div>
          </div>

          {/* Body */}
          <div style={S.sidePanelBody}>
            {/* TAB 0: 詳細 */}
            {sidePanelTab === 0 && (
              <div style={{ padding: '16px 20px' }}>
                {/* 候補 summary */}
                <div style={{ marginBottom: '12px' }}>
                  <SubSectionTitle headlineLevel={4}>候補サマリ</SubSectionTitle>
                  <ColumnBase paddingSize="small" border="default" rounded>
                    <VStack gap={0.25}>
                      <div style={S.metaRow}>
                        <span style={S.metaLabel}>partner</span>
                        <span style={S.metaValue}>
                          {r.partner_name || '—'} {r.partner_id ? `(pid:${r.partner_id})` : <span style={{ color: '#8a8580' }}>(未登録)</span>}
                        </span>
                      </div>
                      <div style={S.metaRow}>
                        <span style={S.metaLabel}>walletable</span>
                        <span style={S.metaValue}>{walletableName(r.walletable_id)} (id:{r.walletable_id})</span>
                      </div>
                      <div style={S.metaRow}>
                        <span style={S.metaLabel}>flow_type</span>
                        <span style={S.metaValue}>{r.flow_type}</span>
                      </div>
                      <div style={S.metaRow}>
                        <span style={S.metaLabel}>match</span>
                        <span style={{ ...S.metaValue, fontFamily: 'monospace' }}>
                          {r.match_type} / `{r.description_pattern}`
                        </span>
                      </div>
                      <div style={S.metaRow}>
                        <span style={S.metaLabel}>sample</span>
                        <span style={{ ...S.metaValue, fontFamily: 'monospace', fontSize: '0.6875rem' }}>
                          {r.sample_description || '—'}
                        </span>
                      </div>
                      <div style={S.metaRow}>
                        <span style={S.metaLabel}>金額レンジ</span>
                        <span style={{ ...S.metaValue, fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(r.amount_stats.min)} 〜 {fmt(r.amount_stats.max)} (cv:{r.amount_stats.cv})
                        </span>
                      </div>
                      <div style={S.metaRow}>
                        <span style={S.metaLabel}>発生頻度</span>
                        <span style={S.metaValue}>
                          月平均 {r.occurrence.monthly_avg} 件 / 期間 {r.occurrence.count_window} 件
                        </span>
                      </div>
                      <div style={S.metaRow}>
                        <span style={S.metaLabel}>期間</span>
                        <span style={S.metaValue}>{r.occurrence.first_date} 〜 {r.occurrence.last_date}</span>
                      </div>
                      <div style={S.metaRow}>
                        <span style={S.metaLabel}>信頼度</span>
                        <span style={{ ...S.metaValue, color: confidenceColor(r.pattern_confidence) }}>
                          <b>{Math.round(r.pattern_confidence * 100)}%</b> / source: {r.pattern_source}
                        </span>
                      </div>
                    </VStack>
                  </ColumnBase>
                </div>

                {/* 想定仕訳 (推奨) */}
                <div style={{ marginBottom: '12px' }}>
                  <SubSectionTitle headlineLevel={4}>想定仕訳 (推奨)</SubSectionTitle>
                  <ColumnBase paddingSize="zero" border="default" rounded>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={S.detailTh}>借方科目</th>
                          <th style={S.detailTh}>税区分</th>
                          <th style={S.detailTh}>部門</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={S.detailTd}>
                            <Text color="burnt">
                              {r.recommended.account_item_name || '—'}
                            </Text>
                          </td>
                          <td style={S.detailTd}>{r.recommended.tax_code ?? '—'}</td>
                          <td style={S.detailTd}>{r.recommended.section_name || '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </ColumnBase>
                </div>

                {r.registry && (
                  <MessageBlock notice mb={0.5}>
                    <div style={{ fontSize: '0.75rem' }}>
                      <b>Registry seed 一致: </b>{r.registry.match_rule}
                      {r.registry.notes && <div style={{ marginTop: '4px', color: '#6b6560' }}>{r.registry.notes}</div>}
                    </div>
                  </MessageBlock>
                )}

                {/* Action buttons */}
                <div style={{ marginTop: '16px' }}>
                  <HStack gap={0.5}>
                    {r.status !== 'active' && r.status !== 'manual_confirmed' && (
                      <Button appearance="primary" disabled={working} onClick={() => onMarkRegistered(r)}>
                        登録済みに変更
                      </Button>
                    )}
                    {(r.status === 'active' || r.status === 'manual_confirmed') && (
                      <Button appearance="secondary" danger disabled={working} onClick={() => onMarkUnregistered(r)}>
                        未設定に戻す
                      </Button>
                    )}
                    <a
                      href={freeeRuleUrl(r.walletable_id)}
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <Button appearance="secondary">freee UI を開く ↗</Button>
                    </a>
                  </HStack>
                </div>
              </div>
            )}

            {/* TAB 1: 設定手順 */}
            {sidePanelTab === 1 && (
              <div style={{ padding: '16px 20px' }}>
                <SubSectionTitle headlineLevel={4}>freee 設定手順 (6 Steps)</SubSectionTitle>
                {r.registry?.notes && (
                  <MessageBlock notice mb={0.5}>
                    <b>注意: </b>{r.registry.notes}
                  </MessageBlock>
                )}
                <VStack gap={0.5}>
                  <MessageBlock info>
                    <div>
                      <span style={S.stepNumBadge}>1</span>
                      <b>freee UI を開く</b> — 対象口座の自動登録ルール画面へ遷移。
                      <div style={{ marginTop: '6px' }}>
                        <a href={freeeRuleUrl(r.walletable_id)} target="_blank" rel="noreferrer" style={{ fontSize: '0.75rem' }}>
                          {freeeRuleUrl(r.walletable_id)}
                        </a>
                      </div>
                    </div>
                  </MessageBlock>
                  <MessageBlock info>
                    <div>
                      <span style={S.stepNumBadge}>2</span>
                      <b>右上「新規作成」→「自動登録ルール」</b>
                      <ul style={{ marginTop: '6px', paddingLeft: '24px', fontSize: '0.75rem' }}>
                        <li>収支区分: {r.entry_side === 'income' ? '入金' : '出金'}</li>
                        <li>取引口座: {walletableName(r.walletable_id)}</li>
                      </ul>
                    </div>
                  </MessageBlock>
                  <MessageBlock info>
                    <div>
                      <span style={S.stepNumBadge}>3</span>
                      <b>条件を設定</b>
                      <ul style={{ marginTop: '6px', paddingLeft: '24px', fontSize: '0.75rem' }}>
                        <li>
                          取引内容: <code style={{ background: '#f5f3f2', padding: '1px 4px', borderRadius: '3px' }}>{r.description_pattern}</code>{' '}
                          を <b>{r.match_type}</b> で含む
                        </li>
                        <li>
                          金額範囲: {fmt(r.amount_stats.min)} 〜 {fmt(r.amount_stats.max)}
                          {r.amount_stats.cv > 0.5 && <span style={{ color: '#c67320' }}> (variance 大 → 空欄推奨)</span>}
                        </li>
                      </ul>
                    </div>
                  </MessageBlock>
                  <MessageBlock info>
                    <div>
                      <span style={S.stepNumBadge}>4</span>
                      <b>登録内容を設定</b>
                      <ul style={{ marginTop: '6px', paddingLeft: '24px', fontSize: '0.75rem' }}>
                        <li>取引先: {r.partner_name || '—'} (pid:{r.partner_id || '—'})</li>
                        <li>勘定科目: {r.recommended.account_item_name || '未取得'} (id:{r.recommended.account_item_id ?? '—'})</li>
                        <li>税区分: {r.recommended.tax_code ?? '—'}</li>
                        <li>部門: {r.recommended.section_name || '—'}</li>
                      </ul>
                    </div>
                  </MessageBlock>
                  <MessageBlock info>
                    <div>
                      <span style={S.stepNumBadge}>5</span>
                      <b>消込設定</b>
                      <ul style={{ marginTop: '6px', paddingLeft: '24px', fontSize: '0.75rem' }}>
                        <li>「不足金額を明細として追加」→ 実行</li>
                        <li>不足金額の科目/税区分は上記と同じ</li>
                        <li>freee 保存後「ルール ID」 (URL 末尾の数字) を控える</li>
                      </ul>
                    </div>
                  </MessageBlock>
                  <MessageBlock success>
                    <div>
                      <span style={S.stepNumBadge}>6</span>
                      <b>FREEEMAN に戻り「登録済みに変更」を押す</b>
                      <div style={{ marginTop: '6px', fontSize: '0.75rem', color: '#6b6560' }}>
                        freee_rule_id を入力 (任意) → state=manual_confirmed に昇格
                      </div>
                    </div>
                  </MessageBlock>
                </VStack>
              </div>
            )}

            {/* TAB 2: 履歴 */}
            {sidePanelTab === 2 && (
              <div style={{ padding: '16px 20px' }}>
                <SubSectionTitle headlineLevel={4}>観察状態</SubSectionTitle>
                {panelEvidence ? (
                  <>
                    <ColumnBase paddingSize="small" border="default" rounded>
                      <VStack gap={0.25}>
                        <div style={S.metaRow}>
                          <span style={S.metaLabel}>observation kind</span>
                          <span style={S.metaValue}>{panelEvidence.observation.kind}</span>
                        </div>
                        <div style={S.metaRow}>
                          <span style={S.metaLabel}>matched / total</span>
                          <span style={S.metaValue}>
                            {panelEvidence.observation.matched || 0} / {panelEvidence.observation.total || 0}
                          </span>
                        </div>
                        <div style={S.metaRow}>
                          <span style={S.metaLabel}>confirm_grace_until</span>
                          <span style={S.metaValue}>{panelEvidence.rule.confirm_grace_until || '—'}</span>
                        </div>
                        <div style={S.metaRow}>
                          <span style={S.metaLabel}>freee_rule_id</span>
                          <span style={S.metaValue}>{panelEvidence.rule.freee_rule_id ?? '—'}</span>
                        </div>
                      </VStack>
                    </ColumnBase>

                    <SubSectionTitle headlineLevel={4}>最近の wallet_txn ({panelEvidence.wallet_txns.length}件)</SubSectionTitle>
                    <ColumnBase paddingSize="zero" border="default" rounded>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={S.detailTh}>日付</th>
                            <th style={S.detailTh}>摘要</th>
                            <th style={{ ...S.detailTh, textAlign: 'right' }}>金額</th>
                            <th style={{ ...S.detailTh, textAlign: 'center' }}>matched</th>
                          </tr>
                        </thead>
                        <tbody>
                          {panelEvidence.wallet_txns.slice(0, 10).map((w) => (
                            <tr key={w.wallet_txn_id}>
                              <td style={{ ...S.detailTd, fontFamily: 'monospace' }}>{w.date}</td>
                              <td style={S.detailTd}>{w.description?.slice(0, 40) || '—'}</td>
                              <td style={{ ...S.detailTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(w.amount)}</td>
                              <td style={{ ...S.detailTd, textAlign: 'center' }}>
                                {w.rule_matched === 1
                                  ? <span style={S.badge('#e6f4ea', '#338650')}>✓</span>
                                  : <span style={S.badge('#fde7e7', '#c33939')}>✗</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ColumnBase>

                    <SubSectionTitle headlineLevel={4}>監査ログ ({panelEvidence.audit_log.length}件)</SubSectionTitle>
                    {panelEvidence.audit_log.length === 0 && <Note>履歴がまだありません</Note>}
                    {panelEvidence.audit_log.slice(0, 20).map((ev) => (
                      <div
                        key={ev.id}
                        style={{
                          padding: '10px 12px',
                          borderLeft: '3px solid #e5e4e2',
                          marginBottom: '8px',
                          background: '#fafaf8',
                        }}
                      >
                        <div style={{ fontSize: '0.75rem', color: '#252525', fontWeight: 600 }}>
                          {ev.from_status ? `${ev.from_status} → ${ev.to_status}` : ev.to_status}
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: '#8a8580', marginTop: '2px' }}>
                          {ev.changed_at} / {ev.changed_by} {ev.cause && `/ ${ev.cause}`}
                        </div>
                        {ev.reason && (
                          <div style={{ fontSize: '0.75rem', color: '#6b6560', marginTop: '4px' }}>
                            {ev.reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <Note>候補のみの行は履歴がありません。「登録済みに変更」を押すと初回 evidence が生成されます。</Note>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------
  const tier = walletableTier(selectedWalletable);

  return (
    <div style={S.page}>
      <Container width="wide">
        {/* Breadcrumbs */}
        <div style={{ padding: '16px 0 8px' }}>
          <Breadcrumbs
            links={[
              { title: 'FREEEMAN', url: '/' },
              { title: '自動登録ルール' },
              ...(selectedWalletable ? [{ title: walletableName(selectedWalletable) }] : []),
            ]}
          />
        </div>

        {/* Page title */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <HStack gap={1} alignItems="center">
              <PageTitle>自動登録ルール一覧</PageTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={S.badge('#f0f9ff', '#0284c7')}>{walletableName(selectedWalletable)}</span>
                {tier && <span style={S.badge(tierColor(tier).bg, tierColor(tier).color)}>{tier}</span>}
              </div>
            </HStack>
            <HStack gap={0.5}>
              <SelectBox
                options={walletableOptions}
                value={selectedWalletable ? String(selectedWalletable) : 'all'}
                onChange={(e) => setSelectedWalletable(e.target.value === 'all' ? null : Number(e.target.value))}
                width="medium"
                small
                label="口座"
              />
              <SelectBox
                options={WTYPE_OPTIONS}
                value={selectedWalletableType}
                onChange={(e) => setSelectedWalletableType(e.target.value)}
                width="small"
                small
                label="口座種別"
              />
              <Button appearance="secondary" small onClick={loadRows} disabled={loading}>
                {loading ? '読込中…' : '再読込'}
              </Button>
            </HStack>
          </div>
          <div style={{ marginTop: '4px' }}>
            <Note>
              候補 {rows.length}件 / 表示 {filtered.length}件
              {lastLoadedAt && ` / 最終更新 ${lastLoadedAt}`}
            </Note>
          </div>
        </div>

        {/* Error banner */}
        {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}

        {/* KPI Cards */}
        <div style={S.kpiGrid}>
          {renderKpiCard('候補総数', `${stats.total}件`, '#0284c7')}
          {renderKpiCard('未設定', `${stats.unregistered}件`, '#c33939', 'freeeで設定必要')}
          {renderKpiCard('設定済', `${stats.registered}件`, '#338650', stats.total > 0 ? `${Math.round((stats.registered / stats.total) * 100)}%` : '0%')}
          {renderKpiCard('対応待ち(ルール判定)', `${stats.pending}件`, '#a46a12', 'ルール化する/しないを判定 (この表)')}
          {renderKpiCard('今月発生', `${stats.thisMonth}件`, '#2e7cf6')}
        </div>

        {/* Info message */}
        <MessageBlock info mb={0.5}>
          freee API の user_matchers は OAuth 403 のため、FREEEMAN は「候補抽出 + user 手動 mark」でルール設定状況を管理します。
          freee UI でルールを設定したら、本ページで「登録済みに変更」ボタンを押してください。
        </MessageBlock>

        {/* Filter bar */}
        <CardBase paddingSize="zero" mb={0.5}>
          <div style={S.filterBar}>
            <SearchField
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="partner / 摘要で検索"
              width="large"
              small
            />
            <SelectBox
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              width="small"
              small
              label="状態"
            />
            <SelectBox
              options={SORT_OPTIONS}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              width="small"
              small
              label="並び"
            />
            <HStack gap={0.25} alignItems="center">
              <label style={{ fontSize: '0.75rem', color: '#6b6560' }}>最低発生件数</label>
              <input
                type="number"
                min={1}
                max={20}
                value={minOccurrence}
                onChange={(e) => setMinOccurrence(Number(e.target.value) || 3)}
                style={{
                  width: '60px',
                  padding: '5px 8px',
                  fontSize: '0.75rem',
                  border: '1px solid #dbd9d7',
                  borderRadius: '4px',
                }}
              />
            </HStack>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckBox
                checked={onlyRegistrySeed}
                onChange={() => setOnlyRegistrySeed(!onlyRegistrySeed)}
                small
              >
                registry seed のみ
              </CheckBox>
              <Button appearance="tertiary" small onClick={onExportCsv}>
                CSV エクスポート
              </Button>
            </div>
          </div>
        </CardBase>

        {/* ============================================ */}
        {/* TOP: 新規 自動登録ルール 作成 form              */}
        {/* ============================================ */}
        <div style={{ marginBottom: '16px' }}>
          <CardBase>
            <div style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <HStack gap={0.5} alignItems="center">
                  <MdAddCircle size={20} color="#2e7cf6" />
                  <Text weight="bold" size={1}>新規 自動登録ルール 作成</Text>
                  <Note>登録すると freee に push、 翌月以降の同 pattern wallet_txn が auto 仕訳化</Note>
                </HStack>
                <Button appearance="primary" small onClick={() => setFormOpen(!formOpen)}>
                  {formOpen ? '閉じる' : '新規作成 form を開く'}
                </Button>
              </div>
              {formOpen && (
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '4px', marginTop: '8px', boxSizing: 'border-box' }}>
                  {formError && (
                    <div style={{ padding: '8px', background: '#fee2e2', color: '#b91c1c', marginBottom: '8px', borderRadius: '4px', fontSize: '0.8rem' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <MdWarning size={14} /> {formError}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px', boxSizing: 'border-box' }}>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>登録タイプ (act) *</label>
                      <select value={String(formAct)} onChange={(e) => setFormAct(Number(e.target.value))}
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box', background: 'white' }}>
                        {formActOptions.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                      </select>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>マッチ条件 (condition) *</label>
                      <select value={String(formCondition)} onChange={(e) => setFormCondition(Number(e.target.value))}
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box', background: 'white' }}>
                        {formConditionOptions.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                      </select>
                    </div>
                    <div style={{ gridColumn: 'span 2', minWidth: 0 }}>
                      <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>摘要 pattern (description) *</label>
                      <input type="text" value={formDescription} onChange={(e) => setFormDescription(e.target.value)}
                        placeholder="例: ALSOK / コクサイ / 株式会社..."
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>入出金 *</label>
                      <select value={formEntrySide} onChange={(e) => setFormEntrySide(e.target.value as 'income' | 'expense')}
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box', background: 'white' }}>
                        <option value="expense">出金 (expense)</option>
                        <option value="income">入金 (income)</option>
                      </select>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>口座 (walletable)</label>
                      <select value={formWalletable} onChange={(e) => setFormWalletable(e.target.value)}
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box', background: 'white' }}>
                        {formWalletableOptions.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                      </select>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>最小金額</label>
                      <input type="number" value={formMinAmount} onChange={(e) => setFormMinAmount(e.target.value)}
                        placeholder="(指定なし)"
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>最大金額</label>
                      <input type="number" value={formMaxAmount} onChange={(e) => setFormMaxAmount(e.target.value)}
                        placeholder="(指定なし)"
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>
                    {[0, 1].includes(formAct) && (
                      <>
                        <div style={{ minWidth: 0 }}>
                          <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>勘定科目 * (act=0,1)</label>
                          <select value={formAccountItemName} onChange={(e) => setFormAccountItemName(e.target.value)}
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box', background: 'white' }}>
                            {formAccountItemOptions.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                          </select>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>税区分 * (act=0,1)</label>
                          <select value={formTaxName} onChange={(e) => setFormTaxName(e.target.value)}
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box', background: 'white' }}>
                            {formTaxOptions.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                          </select>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>取引先名 ({partnersList.length}件 from freee)</label>
                          <input type="text" value={formPartnerName} onChange={(e) => setFormPartnerName(e.target.value)}
                            list="form-partners-datalist" placeholder="既存 partner から選択推奨"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                          <datalist id="form-partners-datalist">
                            {partnersList.map(p => <option key={p.id} value={p.name}>{p.long_name || p.name}</option>)}
                          </datalist>
                          {partnersLoading && <Note>partners list 取得中…</Note>}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>部門</label>
                          <select value={formSectionName} onChange={(e) => setFormSectionName(e.target.value)}
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box', background: 'white' }}>
                            {formSectionOptions.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                          </select>
                        </div>
                      </>
                    )}
                    <div>
                      <label style={{ fontSize: '0.7rem', color: '#666' }}>優先度 (priority) *</label>
                      <input type="number" value={formPriority} onChange={(e) => setFormPriority(Number(e.target.value) || 50)}
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.7rem', color: '#666' }}>有効</label>
                      <div style={{ padding: '6px 0' }}>
                        <CheckBox checked={formActive} onChange={() => setFormActive(!formActive)} small>有効化</CheckBox>
                      </div>
                    </div>
                    <div style={{ gridColumn: 'span 2', minWidth: 0 }}>
                      <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>取引備考 (deal_description) — 自動登録時に取引摘要として記録</label>
                      <input type="text" value={formDealDescription} onChange={(e) => setFormDealDescription(e.target.value)}
                        placeholder="(空欄可)"
                        style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>

                    {/* === 振替系 (act=2,3) 振替先口座 === */}
                    {[2, 3].includes(formAct) && (
                      <div style={{ gridColumn: 'span 2', minWidth: 0, padding: '8px', background: '#fef9e7', borderRadius: '4px' }}>
                        <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>振替先口座 (transfer_walletable) * (act=2,3 必須)</label>
                        <select value={formTransferWalletable} onChange={(e) => setFormTransferWalletable(e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box', background: 'white' }}>
                          {formWalletableOptions.map(o => <option key={o.value} value={o.value}>{o.name}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* === Advanced fields toggle === */}
                  <div style={{ marginTop: '12px', borderTop: '1px dashed #dbd9d7', paddingTop: '8px' }}>
                    <Button appearance="tertiary" small onClick={() => setFormAdvancedOpen(!formAdvancedOpen)}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {formAdvancedOpen ? <MdExpandMore size={16} /> : <MdChevronRight size={16} />}
                        Advanced (品目 / インボイス / セグメント / メモタグ等)
                      </span>
                    </Button>
                    {formAdvancedOpen && (
                      <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px', padding: '8px', background: '#fdfdfd', borderRadius: '4px' }}>
                        {[0, 1].includes(formAct) && (
                          <>
                            <div style={{ minWidth: 0 }}>
                              <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>
                                品目名 (item_name) <span style={{ color: '#ef4444' }}>[注意] 未登録名は freee で auto-create</span>
                              </label>
                              <input type="text" value={formItemName} onChange={(e) => setFormItemName(e.target.value)}
                                placeholder="(空欄推奨)"
                                style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>適格請求書 (qualified_invoice_setting)</label>
                              <select value={formQualifiedInvoice} onChange={(e) => setFormQualifiedInvoice(e.target.value as 'non_qualified' | 'qualified' | 'depends_on_partner' | '')}
                                style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box', background: 'white' }}>
                                <option value="">指定なし</option>
                                <option value="non_qualified">該当しない</option>
                                <option value="qualified">該当する</option>
                                <option value="depends_on_partner">取引先情報に準拠</option>
                              </select>
                            </div>
                          </>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>カードラベル (card_label)</label>
                          <input type="text" value={formCardLabel} onChange={(e) => setFormCardLabel(e.target.value)}
                            placeholder="(空欄可)"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                        </div>
                        {[0, 1].includes(formAct) && (
                          <>
                            <div style={{ minWidth: 0 }}>
                              <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>セグメント1 [注意] auto-create</label>
                              <input type="text" value={formDivisionTag1} onChange={(e) => setFormDivisionTag1(e.target.value)}
                                style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>セグメント2 [注意 auto-create]</label>
                              <input type="text" value={formDivisionTag2} onChange={(e) => setFormDivisionTag2(e.target.value)}
                                style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>セグメント3 ⚠</label>
                              <input type="text" value={formDivisionTag3} onChange={(e) => setFormDivisionTag3(e.target.value)}
                                style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ gridColumn: 'span 2', minWidth: 0 }}>
                              <label style={{ fontSize: '0.7rem', color: '#666', display: 'block', marginBottom: '2px' }}>メモタグ (default_tag_names) ⚠ カンマ区切り</label>
                              <input type="text" value={formDefaultTagNames} onChange={(e) => setFormDefaultTagNames(e.target.value)}
                                placeholder="例: タグA, タグB"
                                style={{ width: '100%', padding: '6px 8px', fontSize: '0.8rem', border: '1px solid #dbd9d7', borderRadius: '4px', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ gridColumn: 'span 2', minWidth: 0 }}>
                              <CheckBox checked={formSuggestTaxFromWalletable} onChange={() => setFormSuggestTaxFromWalletable(!formSuggestTaxFromWalletable)} small>
                                購入データ原本に準拠 (suggest_tax_from_walletable_invoice)
                              </CheckBox>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button appearance="tertiary" small onClick={resetForm}>クリア</Button>
                    <Button appearance="tertiary" small onClick={() => setFormOpen(false)}>キャンセル</Button>
                    <Button appearance="primary" small disabled={formSubmitting} onClick={submitForm}>
                      {formSubmitting ? '作成中…' : 'freee に作成'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardBase>
        </div>

        {/* Bulk action bar */}
        {selectedFingerprints.size > 0 && (
          <MessageBlock success mb={0.5}>
            <HStack gap={1} alignItems="center">
              <Text weight="bold" size={0.875}>{selectedFingerprints.size}件選択中</Text>
              <Button appearance="primary" small disabled={bulkWorking} onClick={onBulkMarkRegistered}>
                選択を一括「登録済み」化
              </Button>
              {selectedWalletable && (
                <a
                  href={freeeRuleUrl(selectedWalletable)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Button appearance="secondary" small>
                    freee UI を新タブで開く ↗
                  </Button>
                </a>
              )}
              <Button appearance="tertiary" small onClick={() => setSelectedFingerprints(new Set())}>
                選択解除
              </Button>
            </HStack>
          </MessageBlock>
        )}

        {/* Main two-column */}
        <div style={activePanelFp ? S.mainLayoutWithPanel : S.mainLayout}>
          {/* LEFT: Table */}
          <CardBase paddingSize="zero" overflowHidden={false}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
                <thead>
                  <tr>
                    <th style={{ ...S.thCell, width: '36px', textAlign: 'center' }}>
                      <CheckBox
                        checked={filtered.length > 0 && selectedFingerprints.size === filtered.length}
                        onChange={toggleSelectAll}
                        small
                      />
                    </th>
                    <th style={{ ...S.thCell, width: '100px' }}>状態</th>
                    <th style={{ ...S.thCell, width: '180px' }}>取引先</th>
                    <th style={{ ...S.thCell, width: '90px' }}>flow_type</th>
                    <th style={{ ...S.thCell, width: '130px' }}>口座</th>
                    <th style={{ ...S.thCell, width: '240px' }}>想定 match</th>
                    <th style={{ ...S.thCell, width: '160px' }}>勘定科目</th>
                    <th style={{ ...S.thCell, width: '70px', textAlign: 'center' }}>税区分</th>
                    <th style={{ ...S.thCell, width: '120px' }}>部門</th>
                    <th style={{ ...S.thCell, width: '90px', textAlign: 'right' }}>頻度</th>
                    <th style={{ ...S.thCell, width: '60px', textAlign: 'center' }}>信頼</th>
                    <th style={{ ...S.thCell, width: '90px' }}>source</th>
                    <th style={{ ...S.thCell, width: '110px' }}>最終確認</th>
                    <th style={{ ...S.thCell, width: '210px', textAlign: 'center' }}>アクション</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const isActive = activePanelFp === r.rule_fingerprint;
                    const isSelected = selectedFingerprints.has(r.rule_fingerprint);
                    const stB = statusBadge(r.status);
                    const ftColor = flowTypeColor(r.flow_type);
                    const tierB = walletableTier(r.walletable_id);
                    return (
                      <tr
                        key={r.rule_fingerprint}
                        style={S.row(isSelected, isActive)}
                        onClick={() => handleRowClick(r)}
                      >
                        <td style={{ ...S.tdCell, textAlign: 'center', width: '36px' }} onClick={(e) => e.stopPropagation()}>
                          <CheckBox checked={isSelected} onChange={() => toggleSelect(r.rule_fingerprint)} small />
                        </td>
                        <td style={{ ...S.tdCell, textAlign: 'center' }}>
                          <span style={S.badge(stB.bg, stB.color)}>{stB.label}</span>
                        </td>
                        <td style={S.tdCell}>
                          <div style={{ fontWeight: 600, color: '#252525', fontSize: '0.8125rem' }}>
                            {r.partner_name || <span style={{ color: '#c4c0bc' }}>（未登録）</span>}
                          </div>
                          <div style={{ marginTop: '2px', fontSize: '0.6875rem', color: '#8a8580', fontFamily: 'monospace' }}>
                            {r.partner_id ? `pid:${r.partner_id}` : ''}
                          </div>
                        </td>
                        <td style={S.tdCell}>
                          <span style={S.badge(ftColor.bg, ftColor.color)}>
                            {flowTypeLabel(r.flow_type)}
                          </span>
                        </td>
                        <td style={S.tdCell}>
                          <div style={{ fontSize: '0.75rem', color: '#252525' }}>
                            {walletableName(r.walletable_id)}
                          </div>
                          {tierB && (
                            <span style={{ ...S.flagBadge, background: tierColor(tierB).bg, color: tierColor(tierB).color, marginTop: '2px' }}>
                              {tierB}
                            </span>
                          )}
                        </td>
                        <td style={S.tdCell}>
                          <code style={{
                            fontSize: '0.75rem',
                            background: '#f5f3f2',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            color: '#252525',
                            wordBreak: 'break-all',
                          }}>
                            {r.description_pattern}
                          </code>
                          <span style={{ ...S.flagBadge, marginLeft: '4px' }}>
                            {r.match_type}
                          </span>
                        </td>
                        <td style={S.tdCell}>
                          <Text size={0.75}>
                            {r.recommended.account_item_name || <span style={{ color: '#c4c0bc' }}>—</span>}
                          </Text>
                        </td>
                        <td style={{ ...S.tdCell, textAlign: 'center', fontFamily: 'monospace', fontSize: '0.6875rem' }}>
                          {r.recommended.tax_code ?? '—'}
                        </td>
                        <td style={S.tdCell}>
                          <Text size={0.75}>{r.recommended.section_name || <span style={{ color: '#c4c0bc' }}>—</span>}</Text>
                        </td>
                        <td style={{ ...S.tdCell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          <div style={{ color: frequencyColor(r.occurrence.monthly_avg), fontWeight: 600, fontSize: '0.75rem' }}>
                            月 {r.occurrence.monthly_avg} 件
                          </div>
                          <div style={{ fontSize: '0.625rem', color: '#8a8580' }}>
                            計 {r.occurrence.count_window} / {fmt(r.amount_stats.total)}
                          </div>
                        </td>
                        <td style={{ ...S.tdCell, textAlign: 'center' }}>
                          <span style={S.badge('transparent', confidenceColor(r.pattern_confidence))}>
                            <b>{Math.round(r.pattern_confidence * 100)}%</b>
                          </span>
                        </td>
                        <td style={{ ...S.tdCell, fontSize: '0.6875rem', color: '#6b6560' }}>
                          {r.pattern_source}
                          {r.registry && <span style={{ marginLeft: '4px' }}>◉</span>}
                        </td>
                        <td style={{ ...S.tdCell, fontFamily: 'monospace', fontSize: '0.625rem', color: '#6b6560' }}>
                          {r.existing_rule?.confirmed_at?.slice(0, 16) || r.existing_rule?.last_observed_at?.slice(0, 16) || '—'}
                        </td>
                        <td style={{ ...S.tdCell, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          {renderActionCell(r)}
                        </td>
                      </tr>
                    );
                  })}

                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={14} style={{ padding: '48px 24px', textAlign: 'center' }}>
                        <VStack gap={0.5} alignItems="center">
                          <Text size={0.875} color="burnt">
                            {loading ? '読込中…' : '該当する候補がありません'}
                          </Text>
                          <Note>フィルターを緩めるか、最低発生件数を下げてみてください</Note>
                        </VStack>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBase>

          {/* RIGHT: Side panel */}
          {activePanelFp && renderSidePanel()}
        </div>

        {/* ============================================ */}
        {/* BOTTOM: freee 既存自動登録ルール一覧            */}
        {/* ============================================ */}
        <div style={{ marginTop: '24px', marginBottom: '16px' }}>
          <CardBase>
            <div style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                <HStack gap={0.5} alignItems="center">
                  <Text weight="bold" size={1}>freee 既存自動登録ルール</Text>
                  <span style={S.badge('#e0f2fe', '#0369a1')}>
                    {freeeLoading ? '取得中…' : `${filteredFreeeMatchers.length} / ${freeeMatchers.length} 件`}
                  </span>
                  {freeeError && <span style={S.badge('#fee2e2', '#b91c1c')}>error: {freeeError}</span>}
                </HStack>
                <HStack gap={0.5}>
                  <input
                    type="text"
                    placeholder="検索 (摘要 / 取引先 / 口座 / 勘定科目)"
                    value={freeeFilter}
                    onChange={(e) => setFreeeFilter(e.target.value)}
                    style={{ padding: '5px 8px', fontSize: '0.75rem', border: '1px solid #dbd9d7', borderRadius: '4px', minWidth: '240px' }}
                  />
                  <SelectBox
                    options={[
                      { value: 'all', name: '全 act' },
                      ...Object.entries(USER_MATCHER_ACT_NAMES).map(([k, v]) => ({ value: k, name: `${k}: ${v}` })),
                    ]}
                    value={String(freeeActFilter)}
                    onChange={(e) => setFreeeActFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                    width="medium"
                    small
                  />
                  <Button appearance="tertiary" small onClick={loadFreeeMatchers} disabled={freeeLoading}>
                    再取得
                  </Button>
                </HStack>
              </div>
              <div style={{ maxHeight: freeeShowAll ? 'none' : '480px', overflowY: 'auto', border: '1px solid #e8e6e3', borderRadius: '4px' }}>
                <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#fafafa', zIndex: 1 }}>
                    <tr style={{ borderBottom: '2px solid #cbd5e0' }}>
                      <th colSpan={5} style={{ padding: '6px 8px', textAlign: 'center', borderRight: '1px solid #ddd', background: '#eff6ff', color: '#1e40af', fontWeight: 700 }}>
                        条件 (= match)
                      </th>
                      <th colSpan={2} style={{ padding: '6px 8px', textAlign: 'center', background: '#fef3c7', color: '#92400e', fontWeight: 700 }}>
                        条件一致時の処理 (= action)
                      </th>
                      <th style={{ background: '#fafafa' }} />
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <th style={{ padding: '4px 6px', textAlign: 'left', width: '50px', fontSize: '0.7rem' }}>id</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left', width: '90px', fontSize: '0.7rem' }}>入出金</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left', minWidth: '140px', fontSize: '0.7rem' }}>摘要 + 一致条件</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left', width: '100px', fontSize: '0.7rem' }}>口座</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right', width: '110px', fontSize: '0.7rem', borderRight: '1px solid #ddd' }}>金額範囲</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left', width: '110px', fontSize: '0.7rem' }}>act</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left', minWidth: '300px', fontSize: '0.7rem' }}>処理内容 (勘定科目 / 税区分 / 取引先 / 部門 / 品目 / その他)</th>
                      <th style={{ padding: '4px 6px', textAlign: 'center', width: '90px', fontSize: '0.7rem' }}>状態</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFreeeMatchers.slice(0, freeeShowAll ? Infinity : 50).map(m => {
                      const isAction = m.act === 1 || m.act === 3;  // 完全自動 (取引/振替 登録)
                      const isSuggest = m.act === 0 || m.act === 2;  // 推測のみ
                      const isIgnore = m.act === 4 || m.act === 10;  // 無視
                      const isPrivate = m.act === 11 || m.act === 12;
                      const actBg = isAction ? '#dcfce7' : isSuggest ? '#dbeafe' : isIgnore ? '#f3f4f6' : isPrivate ? '#fce7f3' : '#fef3c7';
                      const actFg = isAction ? '#166534' : isSuggest ? '#1e40af' : isIgnore ? '#4b5563' : isPrivate ? '#9d174d' : '#92400e';

                      // 処理内容 fields (= 条件一致時に何を行うか、 MD icon 化)
                      const actionParts: { icon: React.ReactNode; text: string }[] = [];
                      if (m.account_item_name) actionParts.push({ icon: <MdMenuBook size={12} />, text: m.account_item_name });
                      if (m.tax_name) actionParts.push({ icon: <MdAttachMoney size={12} />, text: m.tax_name });
                      if (m.partner_name) actionParts.push({ icon: <MdBusiness size={12} />, text: m.partner_name });
                      if (m.section_name) actionParts.push({ icon: <MdLocalOffer size={12} />, text: m.section_name });
                      if (m.item_name) actionParts.push({ icon: <MdInventory2 size={12} />, text: m.item_name });
                      if (m.transfer_walletable) actionParts.push({ icon: <MdArrowForward size={12} />, text: m.transfer_walletable });
                      if (m.deal_description) actionParts.push({ icon: <MdNoteAlt size={12} />, text: m.deal_description.slice(0, 30) });
                      if (m.qualified_invoice_setting && m.qualified_invoice_setting !== 'non_qualified') {
                        const qLabel = m.qualified_invoice_setting === 'qualified' ? '適格' : '取引先準拠';
                        actionParts.push({ icon: <MdReceipt size={12} />, text: qLabel });
                      }
                      if (m.division_tag_1_name) actionParts.push({ icon: <MdLocalOffer size={12} />, text: `seg1=${m.division_tag_1_name}` });
                      if (m.division_tag_2_name) actionParts.push({ icon: <MdLocalOffer size={12} />, text: `seg2=${m.division_tag_2_name}` });
                      if (m.division_tag_3_name) actionParts.push({ icon: <MdLocalOffer size={12} />, text: `seg3=${m.division_tag_3_name}` });
                      if (m.default_tag_names && m.default_tag_names.length > 0) {
                        actionParts.push({ icon: <MdLocalOffer size={12} />, text: `tags: ${m.default_tag_names.join(',')}` });
                      }
                      if (m.card_label) actionParts.push({ icon: <MdReceipt size={12} />, text: `card=${m.card_label}` });
                      if (m.suggest_tax_from_walletable_invoice) actionParts.push({ icon: <MdReceipt size={12} />, text: '原本準拠' });

                      return (
                        <tr key={m.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          <td style={{ padding: '5px 6px', fontFamily: 'monospace', color: '#666', fontSize: '0.7rem' }}>{m.id}</td>
                          <td style={{ padding: '5px 6px' }}>
                            <span style={{ color: m.entry_side_str === 'income' ? '#166534' : '#dc2626', fontWeight: 600 }}>
                              {m.entry_side_str === 'income' ? '入金' : '出金'}
                            </span>
                          </td>
                          <td style={{ padding: '5px 6px' }}>
                            <div style={{ fontFamily: 'monospace', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.description || '-'}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#888' }}>
                              {USER_MATCHER_CONDITION_NAMES[m.condition] || `cond=${m.condition}`}
                            </div>
                          </td>
                          <td style={{ padding: '5px 6px', color: '#666', fontSize: '0.7rem', maxWidth: '110px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.walletable || '指定なし'}
                          </td>
                          <td style={{ padding: '5px 6px', textAlign: 'right', fontFamily: 'monospace', color: '#666', fontSize: '0.65rem', borderRight: '1px solid #ddd' }}>
                            {m.min_amount == null && m.max_amount == null
                              ? '指定なし'
                              : `${m.min_amount != null ? '¥' + m.min_amount.toLocaleString() : '~'} ${m.min_amount != null && m.max_amount != null ? '~' : ''} ${m.max_amount != null ? '¥' + m.max_amount.toLocaleString() : ''}`}
                          </td>
                          <td style={{ padding: '5px 6px' }}>
                            <span style={S.badge(actBg, actFg)}>
                              {USER_MATCHER_ACT_NAMES[m.act] || `act=${m.act}`}
                            </span>
                            <div style={{ fontSize: '0.6rem', color: '#888', marginTop: '2px' }}>
                              priority: {m.priority}
                            </div>
                          </td>
                          <td style={{ padding: '5px 6px' }}>
                            {actionParts.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                                {actionParts.map((p, i) => (
                                  <span key={i} style={{ fontSize: '0.7rem', padding: '2px 6px', background: '#f8fafc', borderRadius: '3px', border: '1px solid #e2e8f0', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                    {p.icon}
                                    {p.text}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#aaa', fontSize: '0.7rem' }}>(処理 fields なし — act={m.act})</span>
                            )}
                          </td>
                          <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: '0.7rem' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                              {m.active ? <MdCheck size={14} color="#338650" /> : <MdClose size={14} color="#999" />}
                              {m.active ? '有効' : '無効'}
                            </div>
                            <Button appearance="tertiary" small onClick={() => onDeleteMatcher(m)}>削除</Button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredFreeeMatchers.length === 0 && !freeeLoading && (
                      <tr><td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
                        {freeeError ? `error: ${freeeError}` : 'ルールなし (= filter で除外 OR freee 側 0件)'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {filteredFreeeMatchers.length > 50 && !freeeShowAll && (
                <div style={{ textAlign: 'center', padding: '6px' }}>
                  <Button appearance="tertiary" small onClick={() => setFreeeShowAll(true)}>
                    残 {filteredFreeeMatchers.length - 50} 件 全表示
                  </Button>
                </div>
              )}
            </div>
          </CardBase>
        </div>

        {/* Footer summary */}
        <div style={S.summaryFooter}>
          <HStack gap={0.5} alignItems="center">
            {selectedFingerprints.size > 0 && (
              <>
                <Button appearance="primary" disabled={bulkWorking} onClick={onBulkMarkRegistered}>
                  選択 {selectedFingerprints.size}件 登録済み化
                </Button>
              </>
            )}
          </HStack>
          <Note>
            未設定 {stats.unregistered} / 設定済 {stats.registered} / 対応待ち(ルール判定) {stats.pending}
          </Note>
        </div>
      </Container>

      {/* Floating toasts */}
      {toasts.map((t) => (
        <FloatingMessageBlock
          key={t.id}
          success={t.kind === 'success'}
          error={t.kind === 'error'}
          notice={t.kind === 'notice'}
          info={t.kind === 'info'}
          onClose={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
        >
          {t.text}
        </FloatingMessageBlock>
      ))}
    </div>
  );
};
